import React from "react";
import { Card, CardBody } from "reactstrap";
import CountUp from "react-countup";

/**
 * Animated stat tile for Mafia/DI landing pages.
 *
 * Pattern: matches the existing DashboardJob/Widgets.js style (card-animate
 * + decorative SVG + CountUp) but with a colored top accent bar so each
 * tile is glanceable at a distance.
 *
 * Props:
 *  - icon:      ri-* icon class
 *  - label:     short uppercase label
 *  - value:     number to count up to
 *  - suffix:    optional unit suffix (e.g. "%", "/day")
 *  - tone:      "primary" | "success" | "info" | "warning" | "danger" | "secondary"
 *  - loading:   bool — render skeleton instead of value
 *  - subtle:    bool — softer styling (no top bar) for less-prominent stats
 *  - onClick:   optional — when set, tile becomes a button-like card
 *  - duration:  CountUp duration in seconds (default 1.4)
 */
const TONE_HEX = {
  primary: "#405189",
  success: "#0ab39c",
  info: "#299cdb",
  warning: "#f7b84b",
  danger: "#f06548",
  secondary: "#74788d",
};

const StatTile = ({
  icon = "ri-stack-line",
  label,
  value,
  suffix = "",
  tone = "primary",
  loading = false,
  subtle = false,
  onClick,
  duration = 1.4,
}) => {
  const hex = TONE_HEX[tone] || TONE_HEX.primary;
  const numericValue = typeof value === "number" ? value : Number(value) || 0;

  const isClickable = typeof onClick === "function";

  return (
    <Card
      className={`card-animate ${isClickable ? "shadow-sm" : ""}`}
      style={{
        cursor: isClickable ? "pointer" : "default",
        overflow: "hidden",
        position: "relative",
        height: "100%",
      }}
      onClick={onClick}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? "button" : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {!subtle && (
        <div
          style={{
            height: 4,
            background: hex,
            width: "100%",
          }}
        />
      )}
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <p
              className="text-uppercase fw-medium text-muted text-truncate mb-2"
              style={{ fontSize: 12, letterSpacing: "0.04em" }}
            >
              {label}
            </p>
            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
              {loading ? (
                <span
                  className="placeholder-glow"
                  style={{ display: "inline-block", minWidth: 60 }}
                >
                  <span className="placeholder col-6" />
                </span>
              ) : (
                <>
                  <CountUp
                    start={0}
                    end={numericValue}
                    duration={duration}
                    separator=","
                  />
                  {suffix}
                </>
              )}
            </h4>
          </div>
          <div
            className="flex-shrink-0 d-inline-flex align-items-center justify-content-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `${hex}1A`, // 10% alpha
              color: hex,
            }}
          >
            <i className={icon} style={{ fontSize: 22 }} />
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default React.memo(StatTile);
